import {
  Spin,
  Button,
  Card,
  Row,
  Table,
  Timeline,
  Tag,
  Alert,
  Space,
  Divider,
  Drawer,
} from "antd";
import {
  DownloadOutlined,
  PlayCircleOutlined,
  FileZipOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import {
  HistoricalDataFileResponse,
  HistoricalDataFilesStatusResponse,
} from "api/generated";
import {
  HISTORICAL_DATA_TYPE,
  useHistoricalData,
} from "hooks/historicalData/useHistoricalData";
import { ReactNode, useEffect, useMemo, useState } from "react";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import {
  generateColumns,
  parseFileToData,
  SummaryRowData,
  TableColumnType,
} from "common/helpers/tableUtils";
import { useSelfPagination } from "hooks/commons/usePaginationTable";

interface GenerateJsonFilesProps {
  executeProcess: () => void;
  downloadHistoricalLayoutFile: (type: HISTORICAL_DATA_TYPE) => void;
  fetchFile: (type: HISTORICAL_DATA_TYPE) => void;
  currentFiles: HistoricalDataFileResponse[];
  loading: boolean;
  jsonSummaryFile: Blob | undefined;
  differencesFile: Blob | undefined;
  filesStatus: HistoricalDataFilesStatusResponse | undefined;
}

function GenerateJsonFiles({
  executeProcess,
  downloadHistoricalLayoutFile,
  fetchFile,
  currentFiles,
  loading,
  jsonSummaryFile,
  differencesFile,
  filesStatus,
}: GenerateJsonFilesProps) {
  const [showTechnicalData, setShowTechnicalData] = useState(false);
  const [summaryData, setSummaryData] = useState<SummaryRowData[]>([]);
  const [differencesData, setDifferencesData] = useState<SummaryRowData[]>([]);
  const [summaryColumns, setSummaryColumns] = useState<TableColumnType[]>([]);
  const [differencesColumns, setDifferencesColumns] = useState<
    TableColumnType[]
  >([]);
  const { processValidationLogs } = useHistoricalData();
  const paginationSummaryConfig = useSelfPagination(summaryData);
  const paginationDifferenceConfig = useSelfPagination(differencesData);

  const jsonLogs: string = useMemo(() => {
    return filesStatus?.jsonLogs ?? "";
  }, [filesStatus]);

  // Check existence of files
  const hasZipFile = useMemo(
    () => currentFiles.some((f) => f.type === HISTORICAL_DATA_TYPE.JSONS_ZIP),
    [currentFiles]
  );
  const hasJsonSummary = useMemo(
    () =>
      currentFiles.some((f) => f.type === HISTORICAL_DATA_TYPE.JSONS_SUMMARY),
    [currentFiles]
  );
  const hasDifferencesFile = useMemo(
    () =>
      currentFiles.some(
        (f) => f.type === HISTORICAL_DATA_TYPE.DIFFERENCES_FILE
      ),
    [currentFiles]
  );

  const creationJsonLogs = useMemo(
    () => processValidationLogs(jsonLogs),
    [jsonLogs]
  );

  /**
   * Determina si una fila tiene errores.
   * Se considera error si el campo "Errores"/"ERRORS" existe con longitud > 4.
   */
  function hasErrors(record: SummaryRowData): boolean {
    const errorField = record["errors"];
    return typeof errorField === "string" && errorField.trim().length > 4;
  }

  /**
   * Ordena los datos para mostrar primero las filas con errores.
   */
  function sortWithErrorsFirst(data: SummaryRowData[]): SummaryRowData[] {
    return [...data].sort((a, b) => {
      const aHasError = hasErrors(a);
      const bHasError = hasErrors(b);
      if (aHasError && !bHasError) return -1;
      if (!aHasError && bHasError) return 1;
      return 0;
    });
  }

  // Procesar summary
  useEffect(() => {
    if (jsonSummaryFile && hasJsonSummary) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const parsedData = parseFileToData(e.target?.result as string);
        setSummaryData(sortWithErrorsFirst(parsedData));
        setSummaryColumns(generateColumns(parsedData));
      };
      reader.readAsText(jsonSummaryFile);
    }
  }, [jsonSummaryFile, hasJsonSummary]);

  // Procesar differences
  useEffect(() => {
    if (differencesFile && hasDifferencesFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const parsedData = parseFileToData(e.target?.result as string);
        setDifferencesData(sortWithErrorsFirst(parsedData));
        setDifferencesColumns(generateColumns(parsedData));
      };
      reader.readAsText(differencesFile);
    }
  }, [differencesFile, hasDifferencesFile]);

  // Auto fetch missing files
  useEffect(() => {
    if (loading) return;
    if (hasJsonSummary && !jsonSummaryFile)
      fetchFile(HISTORICAL_DATA_TYPE.JSONS_SUMMARY);
    if (hasDifferencesFile && !differencesFile)
      fetchFile(HISTORICAL_DATA_TYPE.DIFFERENCES_FILE);
  }, [
    hasJsonSummary,
    hasDifferencesFile,
    fetchFile,
    loading,
    jsonSummaryFile,
    differencesFile,
  ]);

  function getAvailabilityTag(existFile: boolean): ReactNode {
    return existFile ? (
      <Tag icon={<CheckCircleOutlined />} color="success">
        Disponible
      </Tag>
    ) : (
      <Tag icon={<ExclamationCircleOutlined />} color="warning">
        No disponible
      </Tag>
    );
  }

  return (
    <Spin spinning={loading} tip="El proceso puede tomar varios minutos...">
      <Row>
        <Divider />
        <Space
          direction="vertical"
          style={{ width: "100%", textAlign: "right" }}
        >
          <Button
            color="cyan"
            variant="dashed"
            icon={<PlayCircleOutlined />}
            disabled={creationJsonLogs.length === 0}
            onClick={() => {
              setShowTechnicalData(true);
            }}
            loading={loading}
          >
            Mostrar datos técnicos de la generación de Jsons
          </Button>
        </Space>
        <Divider />
      </Row>

      {/* Botones de acción */}
      <Row gutter={12}>
        <ResponsiveCol size="lg">
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={executeProcess}
            loading={loading}
            style={{ width: "100%" }}
            size="large"
          >
            Ejecutar Generación de JSONs
          </Button>
        </ResponsiveCol>
        <ResponsiveCol size="lg">
          <Button
            type="default"
            style={{ width: "100%" }}
            size="large"
            icon={<FileZipOutlined />}
            onClick={() => {
              downloadHistoricalLayoutFile(HISTORICAL_DATA_TYPE.JSONS_ZIP);
            }}
            disabled={!hasZipFile}
          >
            Descargar ZIP
            {hasZipFile && (
              <CheckCircleOutlined style={{ color: "green", marginLeft: 8 }} />
            )}
          </Button>
        </ResponsiveCol>
      </Row>

      {/* Mensajes informativos */}
      {differencesData.length !== 0 && !loading && (
        <div>
          <Divider />
          <Alert
            message="Se han encontrado diferencias"
            description={`El proceso de generacion de jsons obtuvo valores diferentes a los esperados en base a los layouts cargados.
            Puedes intentar solventar el problema regresando al paso de Migracion, y correr la eliminación nuevamente,
            pero si el problema persiste, por favor contacta a soporte para revisar particularmente este caso.
            Al final de esta pagina podras encontrar una tabla con las Diferencias Encontradas, comparte este recurso a soporte para su analisis.
            `}
            type="error"
            showIcon
          />
        </div>
      )}
      <Divider />

      {/* Tabla de Summary */}
      {summaryData.length > 0 && (
        <Row>
          <ResponsiveCol size="xl">
            <Divider style={{ borderColor: "#7cb305" }}>
              Resumen de JSONs Generados
            </Divider>
            <Space
              direction="vertical"
              style={{ width: "100%", textAlign: "right" }}
            >
              <Button
                icon={<DownloadOutlined />}
                type="primary"
                onClick={() =>
                  downloadHistoricalLayoutFile(
                    HISTORICAL_DATA_TYPE.JSONS_SUMMARY
                  )
                }
                children="Descargar"
              />
            </Space>

            <Table
              dataSource={summaryData}
              columns={summaryColumns}
              pagination={paginationSummaryConfig}
              scroll={{ x: true }}
              size="small"
              rowClassName={(record) =>
                hasErrors(record) ? "row-with-inconsistencies" : ""
              }
            />
          </ResponsiveCol>
        </Row>
      )}

      {/* Tabla de Differences */}
      {differencesData.length > 0 && (
        <Row>
          <ResponsiveCol size="xl">
            <Divider style={{ borderColor: "#b31905ff" }}>
              Diferencias Encontradas
            </Divider>
            <Space
              direction="vertical"
              style={{ width: "100%", textAlign: "right" }}
            >
              <Button
                icon={<DownloadOutlined />}
                type="primary"
                onClick={() =>
                  downloadHistoricalLayoutFile(
                    HISTORICAL_DATA_TYPE.DIFFERENCES_FILE
                  )
                }
                children="Descargar"
              />
            </Space>

            <Table
              dataSource={differencesData}
              columns={differencesColumns}
              pagination={paginationDifferenceConfig}
              scroll={{ x: true }}
              size="small"
              rowClassName={(record) =>
                record.Errores || record.Error ? "row-with-inconsistencies" : ""
              }
            />
          </ResponsiveCol>
        </Row>
      )}

      {/* Mensajes informativos */}
      {(!hasZipFile || summaryData.length === 0) && !loading && (
        <Alert
          message="Proceso no ejecutado"
          description="Para generar los archivos JSON, ejecuta el proceso primero. Esto puede tomar varios minutos."
          type="info"
          showIcon
        />
      )}

      <Drawer
        title="Resultado de la creación de Jsons"
        placement="right"
        onClose={() => {
          setShowTechnicalData(false);
        }}
        open={showTechnicalData}
        width={500}
        footer={
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button
              onClick={() => {
                setShowTechnicalData(false);
              }}
            >
              Cerrar
            </Button>
          </Space>
        }
      >
        {/* Estado de archivos */}
        <Card size="small" title="Estado de Archivos">
          <Space direction="vertical" style={{ width: "100%" }}>
            <div>
              <strong>ZIP de JSONs:</strong> {getAvailabilityTag(hasZipFile)}
            </div>

            <div>
              <strong>Summary JSON:</strong>
              {getAvailabilityTag(hasJsonSummary)}
            </div>

            <div>
              <strong>
                Diferencias entre los layouts y la información operativa:
              </strong>
              {getAvailabilityTag(hasDifferencesFile)}
            </div>
          </Space>
        </Card>
        <Divider />
        <Timeline items={creationJsonLogs} />
      </Drawer>
    </Spin>
  );
}

export default GenerateJsonFiles;
